/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiBadge } from '../../badge';
import { EuiI18n } from '../../i18n';
export class EuiComboBoxPill extends Component {
  static defaultProps = {
    color: 'hollow'
  };
  onCloseButtonClick = () => {
    const {
      onClose,
      option
    } = this.props;

    if (onClose) {
      onClose(option);
    }
  };

  render() {
    const {
      asPlainText,
      children,
      className,
      color,
      onClick,
      onClickAriaLabel,
      onClose,
      // eslint-disable-line no-unused-vars
      option,
      // eslint-disable-line no-unused-vars
      ...rest
    } = this.props;
    const classes = classNames('euiComboBoxPill', {
      'euiComboBoxPill--plainText': asPlainText
    }, className);
    const onClickProps = onClick && onClickAriaLabel ? {
      onClick,
      onClickAriaLabel
    } : {};

    if (onClose) {
      return <EuiI18n token="euiComboBoxPill.removeSelection" default="Remove {children} from selection in this group" values={{
        children
      }}>
          {removeSelection => <EuiBadge className={classes} closeButtonProps={{
          tabIndex: -1
        }} color={color} iconOnClick={this.onCloseButtonClick} iconOnClickAriaLabel={removeSelection} iconSide="right" iconType="cross" title={children} {...onClickProps} {...rest}>
              {children}
            </EuiBadge>}
        </EuiI18n>;
    }

    if (asPlainText) {
      return <span className={classes} {...rest}>
          {children}
        </span>;
    }

    return <EuiBadge className={classes} color={color} title={children} {...rest} {...onClickProps}>
        {children}
      </EuiBadge>;
  }

}
EuiComboBoxPill.propTypes = {
  asPlainText: PropTypes.bool,
  children: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.func,
  onClickAriaLabel: PropTypes.any,
  onClose: PropTypes.func,
  option: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any
    }).isRequired),
    value: PropTypes.any
  }).isRequired,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  // @ts-ignore
  EuiComboBoxPill.displayName = "EuiComboBoxPill"; // @ts-ignore

  EuiComboBoxPill.__docgenInfo = {
    "description": "",
    "displayName": "EuiComboBoxPill",
    "props": {
      "asPlainText": {
        "defaultValue": null,
        "description": "",
        "name": "asPlainText",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "color": {
        "defaultValue": {
          value: "hollow"
        },
        "description": "",
        "name": "color",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "onClick": {
        "defaultValue": null,
        "description": "",
        "name": "onClick",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<HTMLButtonElement, MouseEvent>) => void) | undefined"
        }
      },
      "onClickAriaLabel": {
        "defaultValue": null,
        "description": "",
        "name": "onClickAriaLabel",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "onClose": {
        "defaultValue": null,
        "description": "",
        "name": "onClose",
        "required": false,
        "type": {
          "name": "OptionHandler<T> | undefined"
        }
      },
      "option": {
        "defaultValue": null,
        "description": "",
        "name": "option",
        "required": true,
        "type": {
          "name": "EuiComboBoxOptionOption<T>"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/combo_box/combo_box_input/combo_box_pill.tsx#EuiComboBoxPill"] = {
      docgenInfo: EuiComboBoxPill.__docgenInfo,
      name: "EuiComboBoxPill",
      path: "src/components/combo_box/combo_box_input/combo_box_pill.tsx#EuiComboBoxPill"
    };
} catch (__react_docgen_typescript_loader_error) {}